"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Group,
  Button,
  Accordion,
  Paper,
  Anchor,
  Table,
} from "@mantine/core";
import Link from "next/link";
import { trackCtaClick } from "@/lib/analytics";
import classes from "./canm.module.css";

// Section counter helper — matches how-to-do-color-analysis/content.tsx SectionHeader
function SectionHeader({
  n,
  label,
  title,
}: {
  n: number;
  label: string;
  title: string;
}) {
  return (
    <Box mb={24}>
      <p className={classes.kicker}>
        N&ordm; {String(n).padStart(2, "0")} &mdash; {label}
      </p>
      <h2 className={classes.sectionTitle}>{title}</h2>
      <hr className={classes.hairline} />
    </Box>
  );
}

const faqs = [
  {
    question: "How much does color analysis cost near me?",
    answer:
      "Typically $150–$300 in most US cities, and $300–$600+ for well-known analysts in major metros. Exact pricing varies a lot by analyst, city, and how in-demand they are, so treat these as general ranges rather than a quote.",
  },
  {
    question: "Is in-person color analysis worth it?",
    answer:
      "If you want the full physical draping experience and have the budget, yes — a good analyst brings calibrated drapes and a trained outside eye. If you mainly want your season and palette without the cost or wait, an online AI analysis gets you most of the way there for a fraction of the price.",
  },
  {
    question: "How long does a color analysis session take?",
    answer:
      "Most in-person sessions run 60–120 minutes, since the analyst is draping dozens of fabrics one at a time and comparing your reaction. An online AI analysis takes about a minute because it reads undertone, contrast, and depth directly from your photo.",
  },
  {
    question: "What should I wear to a color analysis session?",
    answer:
      "No makeup, so your natural skin tone shows. A neutral or plain-colored top so it doesn't compete with the drapes. And if you can choose the appointment time, natural daylight gives the most accurate reading.",
  },
  {
    question: "Can I get a color analysis done online?",
    answer:
      "Yes. Online AI analysis reads your undertone, contrast, and depth from a couple of photos and returns your 12-season result and palette in about a minute, for a fraction of what an in-person session costs.",
  },
  {
    question: "How do I know if a color analyst is good?",
    answer:
      "Check that they use physical drapes in daylight or color-corrected lighting, not just a screen. Look for training or certification in a recognized system (12-season or 16-season, for example). And read reviews that specifically mention consistent, accurate results across different clients.",
  },
];

export default function ColorAnalysisNearMeContent({ locale }: { locale: string }) {
  void locale;

  let sectionNumber = 0;
  const nextSection = () => {
    sectionNumber += 1;
    return sectionNumber;
  };

  const linkStyle: React.CSSProperties = {
    color: "inherit",
    textDecoration: "underline",
  };

  return (
    <Container size="xl" py={48}>
      <Stack gap={0}>
        {/* ---------- Hero ---------- */}
        <Box className={classes.hero} mb={56}>
          <Box>
            <p className={classes.heroKicker}>FINDING AN ANALYST</p>
            <h1 className={classes.heroTitle}>Color Analysis Near Me</h1>
            <p className={classes.heroTagline}>
              Looking for an in-person color analyst? Here&apos;s what it costs, how to find a
              good one &mdash; and when the online option is the smarter buy.
            </p>

            <Group mt={28}>
              <Button
                component={Link}
                href="/ai-color-analysis"
                onClick={() => trackCtaClick("canm_early")}
                size="md"
                radius={999}
                fw={700}
                styles={{ root: { background: "#141414", color: "white" } }}
              >
                Get analyzed online in ~1 minute
              </Button>
            </Group>
          </Box>
        </Box>

        {/* ---------- N01 What a session is like ---------- */}
        <Box mb={56}>
          <SectionHeader
            n={nextSection()}
            label="The Session"
            title="What an in-person color analysis session is like"
          />
          <Text className={classes.lede} mb={16}>
            An analyst holds fabric drapes up near your face, one at a time, under good lighting
            &mdash; ideally daylight or color-corrected bulbs &mdash; and watches how each one
            changes your skin. The right colors make you look clear and rested; the wrong ones
            pull out shadows, redness, or sallowness.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            A typical session runs 60&ndash;120 minutes. The analyst works through dozens of
            drapes, narrowing from broad warm/cool and light/deep questions down to your specific
            season. You leave with a season result and a palette &mdash; sometimes a physical
            swatch book or fan deck you can take shopping.
          </Text>
        </Box>

        {/* ---------- N02 Cost ---------- */}
        <Box mb={56}>
          <SectionHeader
            n={nextSection()}
            label="Pricing"
            title="What in-person color analysis costs"
          />
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Pricing varies a lot by analyst, city, and demand, but as general ranges: expect
            roughly <Text fw={700} span>$150&ndash;$300</Text> in most US cities, and{" "}
            <Text fw={700} span>$300&ndash;$600+</Text> for well-known analysts in major metros.
            Popular analysts are often booked several weeks out.
          </Text>
          <Text style={{ lineHeight: 1.7, color: "#3a3632" }}>
            These are typical ranges, not quotes &mdash; always confirm price and what&apos;s
            included (palette booklet, follow-up notes, styling advice) directly with the analyst
            before booking.
          </Text>
        </Box>

        {/* ---------- N03 How to find a good analyst ---------- */}
        <Box mb={56}>
          <SectionHeader
            n={nextSection()}
            label="How To Choose"
            title="How to find a good analyst near you"
          />
          <Text mb={20} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Not every analyst is equally rigorous. A short checklist before you book:
          </Text>
          <ul className={classes.numberedList}>
            {[
              'Search "[your city] personal color analysis" rather than a generic style consultant search — it surfaces analysts who specialize in draping.',
              "Confirm they use physical drapes in daylight or color-corrected lighting, not just a screen or an app held up to your face.",
              "Look for training or certification — many analysts credential in a 12-season or 16-season system, which is worth asking about directly.",
              "Read reviews specifically for consistency of results across different clients, not just how nice the appointment felt.",
              "Ask which draping system or method they use before booking, so you know what kind of result to expect.",
            ].map((item, i) => (
              <li key={i} className={classes.numberedItem}>
                <span className={classes.numberFigure}>{String(i + 1).padStart(2, "0")}</span>
                <span className={classes.numberedItemText}>{item}</span>
              </li>
            ))}
          </ul>
        </Box>

        {/* ---------- N04 In-person vs online ---------- */}
        <Box mb={56}>
          <SectionHeader
            n={nextSection()}
            label="Compare"
            title="In-person vs online AI color analysis"
          />
          <Text mb={20} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            In-person is genuinely worth it if you want the full physical experience &mdash; a
            trained eye in the room with you, real fabric under your chin, a booklet to take home
            &mdash; and you have the budget and the weeks to wait for an appointment.
          </Text>

          <Table
            mb={20}
            withRowBorders
            styles={{
              table: { borderCollapse: "collapse" },
              th: {
                fontFamily: "Fraunces, 'Playfair Display', Georgia, serif",
                fontWeight: 500,
                fontSize: 15,
                color: "#1a1a1a",
                borderBottom: "1px solid #e7e2db",
                textAlign: "left",
                padding: "12px 8px",
              },
              td: {
                borderTop: "1px solid #e7e2db",
                fontSize: 14.5,
                color: "#3a3632",
                padding: "12px 8px",
              },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th></Table.Th>
                <Table.Th>In-person</Table.Th>
                <Table.Th>Online AI</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>Price</Table.Td>
                <Table.Td>$150&ndash;$600+</Table.Td>
                <Table.Td>$24.99</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Wait</Table.Td>
                <Table.Td>Often weeks</Table.Td>
                <Table.Td>~1 minute</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Location</Table.Td>
                <Table.Td>Travel required</Table.Td>
                <Table.Td>Anywhere</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Method</Table.Td>
                <Table.Td>Physical drapes held under chin</Table.Td>
                <Table.Td>AI reads undertone, contrast &amp; depth from your photo</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Redo</Table.Td>
                <Table.Td>New paid appointment</Table.Td>
                <Table.Td>Re-run anytime</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          <Text mb={28} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Online is the better fit if you mainly want a fast, accurate read on your season and
            palette without the cost or the wait &mdash; and you can always upgrade to an
            in-person session later if you want the full experience.
          </Text>

          <Box className={`${classes.ctaBand} ${classes.ctaBandSlim}`}>
            <Text className={classes.ctaHeadline} fz={{ base: 18, md: 20 }}>
              Our AI reads the same three properties an analyst does &mdash; undertone, value,
              chroma &mdash; from two photos, in about a minute.
            </Text>
            <Group gap="sm" wrap="wrap" mt="md">
              <Button
                component={Link}
                href="/ai-color-analysis"
                onClick={() => trackCtaClick("canm_mid")}
                size="md"
                radius={999}
                fw={700}
                styles={{ root: { background: "white", color: "#141414" } }}
              >
                Try the online analysis
              </Button>
            </Group>
          </Box>
        </Box>

        {/* ---------- N05 Try online first ---------- */}
        <Box mb={56}>
          <SectionHeader
            n={nextSection()}
            label="Before You Book"
            title="Try the online version first"
          />
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Even if you plan to book an in-person session, it&apos;s worth running the $24.99 AI
            analysis &mdash; or the{" "}
            <Link href="/color-analysis-quiz" style={linkStyle}>
              free color analysis quiz
            </Link>{" "}
            &mdash; first. Either one gives you a season and palette to walk in already informed,
            so you can sanity-check the analyst&apos;s read instead of hearing it cold. Some
            people find the online result is close enough that they skip the appointment
            entirely.
          </Text>
          <Text style={{ lineHeight: 1.7, color: "#3a3632" }}>
            If you want to understand the draping technique itself before you commit either way,
            see how{" "}
            <Link href="/virtual-color-draping" style={linkStyle}>
              virtual color draping
            </Link>{" "}
            works, or read our full{" "}
            <Link href="/how-to-do-color-analysis" style={linkStyle}>
              guide to doing color analysis at home
            </Link>
            .
          </Text>
        </Box>

        {/* ---------- N06 FAQ ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="FAQ" title="Questions people actually ask" />
          <Accordion variant="separated">
            {faqs.map((faq, i) => (
              <Accordion.Item key={i} value={`faq-${i}`}>
                <Accordion.Control>
                  <Text fw={500} className={classes.serif} fz="lg">
                    {faq.question}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="#6f6a64">
                    {faq.question === "Can I get a color analysis done online?" ? (
                      <>
                        Yes.{" "}
                        <Anchor component={Link} href="/ai-color-analysis" fz="sm" c="#6f6a64" td="underline">
                          Online AI analysis
                        </Anchor>{" "}
                        reads your undertone, contrast, and depth from a couple of photos and
                        returns your 12-season result and palette in about a minute, for a
                        fraction of what an in-person session costs.
                      </>
                    ) : (
                      faq.answer
                    )}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* ---------- Bottom CTA ---------- */}
        <Paper className={classes.ctaBand} ta="center" mb={40}>
          <Text
            tt="uppercase"
            fz={11}
            fw={600}
            c="rgba(255,255,255,0.6)"
            style={{ letterSpacing: "0.14em" }}
            mb={8}
          >
            THE SHORTCUT
          </Text>
          <Title order={2} className={classes.ctaHeadline} fz={{ base: 26, md: 34 }}>
            Skip the appointment, keep the result
          </Title>
          <Text className={classes.ctaSubtitle} fz="md" maw={560} mx="auto">
            Get your exact 12-season result, your palette, and the colors to avoid &mdash; from
            two photos, in about a minute, for $24.99.
          </Text>
          <Group justify="center" gap="md" wrap="wrap">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("canm_bottom")}
              size="lg"
              radius={999}
              fw={700}
              styles={{ root: { background: "white", color: "#141414" } }}
            >
              Get your color analysis
            </Button>
          </Group>
          <Text fz="sm" mt={16} style={{ color: "rgba(255,255,255,0.7)" }}>
            Prefer a quiz? Start with the{" "}
            <Link href="/color-analysis-quiz" style={{ color: "inherit", textDecoration: "underline" }}>
              free color analysis quiz
            </Link>
            .
          </Text>
        </Paper>
      </Stack>
    </Container>
  );
}
